import { Navigate } from 'react-router-dom';
import { PageSpinner } from '@/components/ui/page-spinner';
import type { AreaScope } from '@releve/shared';
import { useIsSuperAdmin } from '@/lib/permissions';
import { useCurrentUserOrgRole } from '@/hooks/useCurrentUserOrgRole';
import { useOrgBasePath } from '@/hooks/useOrgNavigate';

interface RequireOrgScopeProps {
  /**
   * The org-role scope the area needs (`@releve/shared` `AreaScope` — every
   * scope but `admin`; admin areas use `RequireOrgAdmin`).
   */
  scope: AreaScope;
  /**
   * Let a platform super admin through whatever their role — set it where the
   * API gate has the same bypass (`requireOrgScope(scope, { superAdminBypass: true })`,
   * e.g. the Audit Log). Without it a super admin is judged like everyone else
   * (a non-member super admin counts as `member`, as in the API).
   */
  superAdminBypass?: boolean;
  children: React.ReactNode;
}

/**
 * Route guard for an org-role scope (docs/USER_ROLES_AND_PERMISSIONS.md):
 * `accounting` for /accounting/* and the accounting settings (Ré is open
 * to every role — its tools are filtered per role on the API); `accounting_admin` for the Audit Log. The sibling of
 * `RequireOrgAdmin` — same shape, same fail-closed loading state, same
 * redirect to the org dashboard. Hiding the nav item is not enforcement; the
 * API's area gate (`area_forbidden`) and `requireOrgScope` are the backstop.
 */
export function RequireOrgScope({ scope, superAdminBypass = false, children }: RequireOrgScopeProps) {
  const { data: isSuperAdmin, isLoading: isSuperAdminLoading } = useIsSuperAdmin();
  const { can, isLoading: isRoleLoading } = useCurrentUserOrgRole();
  const orgBasePath = useOrgBasePath();

  if (isSuperAdminLoading || isRoleLoading) {
    return <PageSpinner />;
  }

  const bypass = superAdminBypass && isSuperAdmin === true;
  if (!can(scope) && !bypass) {
    return <Navigate to={orgBasePath ? `${orgBasePath}/dashboard` : '/'} replace />;
  }

  return <>{children}</>;
}
